import { HttpParams, httpResource } from '@angular/common/http';
import {
  afterRenderEffect,
  Component,
  computed,
  inject,
  Injector,
  input,
  OnInit,
  runInInjectionContext,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { IDropdownOption, MatDropdown } from '../../../../../shared/components/mat-dropdown/mat-dropdown.component';
import { IDropdownSettings } from '../../../../../shared/components/mat-dropdown/scw-mat-dropdown.interface';
import { IGetManyResponse } from '../../../../../utilities/http-utilities.service';
import { IFormAutofill } from '../../../../settings/data-integrations/form-field-mapping/form-autofill.interface';

@Component({
  imports: [MatDropdown],
  selector: 'app-form-autofill-dropdown',
  styleUrl: './form-autofill-dropdown.component.scss',
  templateUrl: './form-autofill-dropdown.component.html',
})
export class FormAutofillDropdownComponent implements OnInit {
  private readonly translate = inject(TranslateService);
  public readonly formControl = input.required<FormControl<IDropdownOption[]>>();
  public readonly selectedIdOnEdit = input.required<number | null>();
  public readonly isFormSubmitted = input.required<boolean>();
  public readonly formId = input<number | null | undefined>(null);
  public readonly dashboardId = input<number | null>(null);
  public readonly workspaceId = input<number | null>(null);

  public readonly searchValue = signal('');
  private previousFormId = signal<number | null | undefined>(undefined);

  public readonly formDropdownConfiguration: IDropdownSettings = {
    enableSearchFilter: true,
    hasDelayedDefaultValue: true,
    isClientSide: false,
    repositionDropdownList: true,
    singleSelection: true,
    text: this.translate.instant('page.widgetContent.addEditDialog.formAutofill'),
  };

  public readonly resource = httpResource<IGetManyResponse<IFormAutofill>>(() => {
    return {
      method: 'GET',
      params: new HttpParams()
        .set('limit', 1000)
        .set(
          's',
          JSON.stringify({
            $and: [{ name: { contL: this.searchValue() } }, { formId: this.formId() }],
          }),
        )
        .set(
          'additionalSearch',
          JSON.stringify({
            dashboardId: this.dashboardId(),
            workspaceId: this.workspaceId(),
          }),
        )
        .set('sort', 'name,ASC'),
      reportProgress: true,
      url: 'form-autofills',
    };
  });

  public readonly dropdownOptions = computed(() => {
    return this.resource.value()?.data.map((form) => ({ id: form.id, name: form.name })) ?? [];
  });

  public readonly isLoading = computed(() => this.resource.status() !== 'resolved');

  constructor(private readonly injector: Injector) {
    afterRenderEffect(() => {
      const formId: number | null | undefined = this.formId();
      const prevFormId: number | null | undefined = this.previousFormId();
      const control: FormControl<IDropdownOption[]> = this.formControl();

      if (prevFormId !== undefined && prevFormId !== formId) {
        control.setValue([], { emitEvent: true });
      }

      this.previousFormId.set(formId);

      if (formId) {
        control.enable({ emitEvent: false });
      } else {
        control.disable({ emitEvent: false });
        control.setValue([], { emitEvent: false });
      }
    });
  }

  public ngOnInit(): void {
    runInInjectionContext(this.injector, () =>
      toSignal(this.formControl().valueChanges, {
        initialValue: this.formControl().value,
      }),
    );
  }
}
